import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  mfaVerifyRequestSchema,
  mfaDisableRequestSchema,
  type MfaEnableResponse,
  type MfaVerifyRequest,
  type MfaDisableRequest,
  type MfaVerifySuccessResponse,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { api, ApiError } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth-store';

type Step = 'idle' | 'enrolling' | 'recovery' | 'disabling';

export function MfaSection() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);

  const [step, setStep] = useState<Step>('idle');
  const [enrollment, setEnrollment] = useState<MfaEnableResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const verifyForm = useForm<MfaVerifyRequest>({
    resolver: zodResolver(mfaVerifyRequestSchema),
    defaultValues: { code: '' },
  });

  const disableForm = useForm<MfaDisableRequest>({
    resolver: zodResolver(mfaDisableRequestSchema),
    defaultValues: { password: '' },
  });

  function patchAuthUser(mfaEnabled: boolean) {
    if (!user || !accessToken) return;
    setAuth({ accessToken, user: { ...user, mfaEnabled } });
  }

  async function startEnroll() {
    setError(null);
    try {
      const data = await api<MfaEnableResponse>('/api/auth/mfa/enable', {
        method: 'POST',
        body: {},
      });
      setEnrollment(data);
      setStep('enrolling');
      verifyForm.reset({ code: '' });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function verifyEnroll(values: MfaVerifyRequest) {
    setError(null);
    const confirmed = window.confirm(t('settings.mfa.confirmActivate'));
    if (!confirmed) return;
    try {
      const data = await api<MfaVerifySuccessResponse>('/api/auth/mfa/verify', {
        method: 'POST',
        body: values,
      });
      setRecoveryCodes(data.recoveryCodes);
      setStep('recovery');
      patchAuthUser(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('settings.mfa.errorInvalidCode'));
      } else {
        setError((err as Error).message);
      }
    }
  }

  function finishEnroll() {
    setEnrollment(null);
    setRecoveryCodes(null);
    setStep('idle');
  }

  async function startDisable() {
    setError(null);
    setStep('disabling');
    disableForm.reset({ password: '' });
  }

  async function confirmDisable(values: MfaDisableRequest) {
    setError(null);
    const confirmed = window.confirm(t('settings.mfa.confirmDisable'));
    if (!confirmed) return;
    try {
      await api<{ status: 'ok' }>('/api/auth/mfa/disable', {
        method: 'POST',
        body: values,
      });
      patchAuthUser(false);
      setStep('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('settings.mfa.errorInvalidPassword'));
      } else {
        setError((err as Error).message);
      }
    }
  }

  function copyRecoveryCodes() {
    if (!recoveryCodes) return;
    void navigator.clipboard.writeText(recoveryCodes.join('\n'));
  }

  function downloadRecoveryCodes() {
    if (!recoveryCodes) return;
    const blob = new Blob(
      [
        `produzioneJB — Recovery codes\n`,
        `User: ${user?.email ?? '?'}\n`,
        `Generati il: ${new Date().toISOString()}\n\n`,
        recoveryCodes.join('\n'),
        '\n\nOgni codice e utilizzabile UNA sola volta. Conservali in un posto sicuro (1Password, password manager, cassaforte).\n',
      ],
      { type: 'text/plain;charset=utf-8' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `produzionejb-mfa-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const enabled = user?.mfaEnabled ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.mfa.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">{t('settings.mfa.description')}</p>

        {/* STATO: disabilitato */}
        {step === 'idle' && !enabled && (
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-4">
            <div>
              <p className="font-medium text-slate-900">{t('settings.mfa.statusDisabled')}</p>
              <p className="text-xs text-slate-500">{t('settings.mfa.statusDisabledHint')}</p>
            </div>
            <Button onClick={startEnroll}>{t('settings.mfa.enableButton')}</Button>
          </div>
        )}

        {/* STATO: abilitato */}
        {step === 'idle' && enabled && (
          <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-4">
            <div>
              <p className="font-medium text-emerald-900">{t('settings.mfa.statusEnabled')}</p>
              <p className="text-xs text-emerald-700">{t('settings.mfa.statusEnabledHint')}</p>
            </div>
            <Button variant="danger" onClick={startDisable}>
              {t('settings.mfa.disableButton')}
            </Button>
          </div>
        )}

        {/* STATO: enrolling — mostra QR + verify form */}
        {step === 'enrolling' && enrollment && (
          <div className="space-y-4 rounded-md border border-brand-200 bg-brand-50 p-4">
            <h4 className="font-medium text-slate-900">{t('settings.mfa.enrollStep1Title')}</h4>
            <p className="text-sm text-slate-700">{t('settings.mfa.enrollStep1Desc')}</p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <img
                src={enrollment.qrCodeDataUrl}
                alt="QR code MFA"
                className="h-48 w-48 rounded border border-slate-300 bg-white p-2"
              />
              <div className="flex-1 space-y-2">
                <p className="text-xs text-slate-500">{t('settings.mfa.enrollStep1Manual')}</p>
                <code className="block break-all rounded bg-white px-3 py-2 text-xs font-mono text-slate-900 border border-slate-300">
                  {enrollment.secret}
                </code>
              </div>
            </div>

            <hr className="border-slate-200" />

            <h4 className="font-medium text-slate-900">{t('settings.mfa.enrollStep2Title')}</h4>
            <p className="text-sm text-slate-700">{t('settings.mfa.enrollStep2Desc')}</p>
            <form
              onSubmit={verifyForm.handleSubmit(verifyEnroll)}
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              noValidate
            >
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="mfaCode">{t('settings.mfa.codeLabel')}</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  {...verifyForm.register('code')}
                />
                {verifyForm.formState.errors.code && (
                  <p className="text-xs text-red-600">
                    {verifyForm.formState.errors.code.message}
                  </p>
                )}
              </div>
              <Button type="submit" disabled={verifyForm.formState.isSubmitting}>
                {t('settings.mfa.verifyButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={finishEnroll}
                disabled={verifyForm.formState.isSubmitting}
              >
                {t('common.cancel')}
              </Button>
            </form>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}

        {/* STATO: recovery codes (subito dopo verify success) */}
        {step === 'recovery' && recoveryCodes && (
          <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
            <h4 className="font-medium text-amber-900">{t('settings.mfa.recoveryTitle')}</h4>
            <p className="text-sm text-amber-800">{t('settings.mfa.recoveryDesc')}</p>
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {recoveryCodes.map((code) => (
                <li
                  key={code}
                  className="rounded bg-white px-3 py-2 text-center text-sm font-mono border border-amber-300"
                >
                  {code}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={copyRecoveryCodes}>
                {t('settings.mfa.copyButton')}
              </Button>
              <Button variant="secondary" size="sm" onClick={downloadRecoveryCodes}>
                {t('settings.mfa.downloadButton')}
              </Button>
              <Button size="sm" onClick={finishEnroll}>
                {t('settings.mfa.savedButton')}
              </Button>
            </div>
          </div>
        )}

        {/* STATO: disabling — chiede password */}
        {step === 'disabling' && (
          <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4">
            <h4 className="font-medium text-red-900">{t('settings.mfa.disableTitle')}</h4>
            <p className="text-sm text-red-800">{t('settings.mfa.disableDesc')}</p>
            <form
              onSubmit={disableForm.handleSubmit(confirmDisable)}
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              noValidate
            >
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="disablePassword">{t('settings.mfa.passwordLabel')}</Label>
                <Input
                  id="disablePassword"
                  type="password"
                  autoComplete="current-password"
                  {...disableForm.register('password')}
                />
                {disableForm.formState.errors.password && (
                  <p className="text-xs text-red-600">
                    {disableForm.formState.errors.password.message}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                variant="danger"
                disabled={disableForm.formState.isSubmitting}
              >
                {t('settings.mfa.disableConfirmButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('idle')}
                disabled={disableForm.formState.isSubmitting}
              >
                {t('common.cancel')}
              </Button>
            </form>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
